import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { catchError, map, Observable, shareReplay, take } from 'rxjs';
import {
  InvoiceListModel,
  InvoiceTypeEnum,
} from '../../../../models/invoice.model';
import { InvoiceService } from '../../../../services/invoice.service';
import { getStatus } from '../../../../utils/invoice.utils';
import { getUserFullName, getUserInitials } from '../../../../utils/user.utils';
import {
  Filter,
  TableColumn,
} from '../../../shared-components/custom-table/custom-table.component';

interface InvoiceListTableModel extends InvoiceListModel {
  creatorFullName?: string;
  initials?: string;
  initialsBackgroundColor?: string;
  initialsColor?: string;
}

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.scss',
  standalone: false,
})
export class InvoicesComponent {
  public dataSource$: Observable<InvoiceListTableModel[]>;
  public isLoading = true;
  private invoices: InvoiceListModel[];
  public selectedYear: number = moment().year();
  public yearOptions: { label: string; value: number }[] = [];

  constructor(
    private invoiceService: InvoiceService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {
    // Generate year options (current year and 5 years back)
    const currentYear = moment().year();
    for (let i = 0; i <= 5; i++) {
      const year = currentYear - i;
      this.yearOptions.push({
        label: year.toString(),
        value: year,
      });
    }
    this.getInvoices();
  }

  private getInvoices() {
    this.isLoading = true;
    this.dataSource$ = this.invoiceService.getAllInvoices().pipe(
      take(1),
      map(invoices => {
        this.isLoading = false;
        this.invoices = invoices;
        return invoices.map(invoice => {
          const initials = getUserInitials(invoice.creator);
          const late =
            invoice.paymentStatus === 'unpaid' &&
            moment(invoice.generatedAt).isBefore(moment().subtract(14, 'days'));
          return {
            ...invoice,
            creatorFullName: getUserFullName(invoice.creator),
            initials: initials?.text,
            initialsBackgroundColor: initials?.backgroundColor,
            initialsColor: initials?.color,
            paymentStatus: late ? 'late' : invoice.paymentStatus,
          };
        });
      }),
      catchError(error => {
        this.isLoading = false;
        console.error('Error fetching seminars', error);
        return [];
      }),
      shareReplay(1) // Cache the result
    );
  }

  public columns: TableColumn[] = [
    {
      columnDef: 'creator',
      header: 'Ersteller',
      width: '20%',
      sortable: true,
      filterValue: row => getUserFullName(row.creator),
      avatarUser: row => row.creator,
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'type',
      header: 'Typ',
      width: '30%',
      sortable: true,
      onCellClick: this.onCellClick.bind(this),
      filterValue: row => this.getGermanInvoiceType(row.type),
      render: row => this.getGermanInvoiceType(row.type),
    },
    {
      columnDef: 'email',
      header: 'E-Mail',
      width: '25%',
      filterValue: row => row?.email,
      render: row => row?.email,
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'invoiceNr',
      header: 'Rechnungsnummer',
      width: '15%',
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'paymentStatus',
      header: 'Status',
      width: '10%',
      filterValue: row => getStatus(row).status,
      render: row => {
        const statusClass = getStatus(row).class;

        return `<div class="${statusClass}">
        ${getStatus(row).status}
      </div>`;
      },
    },
  ];

  public onCellClick(row: InvoiceListModel, column: any) {
    this.router.navigate([row.type, btoa(String(row.id))], {
      relativeTo: this.activatedRoute,
    });
  }

  public filters: Filter[] = [
    {
      filteredColumn: 'paymentStatus',
      type: 'multiselect',
      selectOptions: [
        {
          value: 'paid',
          label: 'Bezahlt',
        },
        {
          value: 'late',
          label: 'Verspätet',
        },
        {
          value: 'unpaid',
          label: 'Ausstehend',
        },
      ],
      placeholder: 'Status auswählen...',
      label: 'Status',
    },
  ];

  private getGermanInvoiceType(type: string) {
    switch (type) {
      case InvoiceTypeEnum.BOOKING:
        return 'Buchung';
      case InvoiceTypeEnum.CANCELLATION:
        return 'Stornierung';
      case InvoiceTypeEnum.COUPON:
        return 'Gutschein';
      default:
        return type;
    }
  }

  public exportInvoicesZip() {
    this.invoiceService
      .exportInvoicesZip(this.selectedYear)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // File download handled in service
        },
        error: error => {
          console.error('Error exporting invoices:', error);
        },
      });
  }
}
